import { useState } from 'react';
import { useFlag } from '@/hooks/useFeatureFlags';
import { useResourceVersionStatus } from '@/hooks/useResourceVersions';
import { PublishDialog } from './PublishDialog';
import { VersionHistoryPanel } from './VersionHistoryPanel';
import { VersionStatusChip } from './VersionStatusChip';
import type { VersionedEntityType } from '@/types/versioning';

/**
 * The versioning affordances a resource's header carries (task 725).
 *
 * One component so every detail and edit view gets the same three things in the
 * same relationship, rather than four pages each assembling them slightly
 * differently:
 *
 *   1. the read-only status indicator (what state is this resource in?),
 *   2. the Publish action, kept visually separate from the indicator because
 *      the indicator is not a button, and
 *   3. the version history panel and publish dialog the two of them open.
 *
 * **Dormant by construction.** When the Account has not adopted Publishing &
 * versions, `useResourceVersionStatus` answers `live-editing` without a network
 * call, and this component renders nothing at all (task 979) — no status, no
 * Publish button, no version vocabulary, nothing to discover. The absence is
 * the signal. The account setting is the user-facing gate; the
 * `innovate.fe.resource-versioning` flag beneath it is the rollback lever,
 * which is why it is baked enabled.
 */

interface ResourceVersionControlsProps {
  entityType: VersionedEntityType;
  entityId: string | undefined;
  /** Resource name, used in the publish dialog and history heading. */
  resourceName?: string;
  /**
   * Hide the status chip and render the Publish action only — for headers that
   * already show the chip elsewhere. Rare; the default shows both.
   */
  actionOnly?: boolean;
  className?: string;
}

export function ResourceVersionControls({
  entityType,
  entityId,
  resourceName,
  actionOnly = false,
  className = '',
}: ResourceVersionControlsProps) {
  const featureEnabled = useFlag('innovate.fe.resource-versioning');
  // Publish, the write, has its own lever (task 1217); the chip and history
  // stay with the capability flag above.
  const publishEnabled = useFlag('innovate.fe.versioning.publish');
  const { status } = useResourceVersionStatus(entityType, entityId);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [publishOpen, setPublishOpen] = useState(false);

  if (!featureEnabled || !entityId) return null;

  const adopted = status.versioningEnabled;
  // Nothing to show a dormant Account (task 979): the chip was this component's
  // only output in that case, and it is gone.
  if (!adopted) return null;

  // Publishing a clean resource freezes nothing new, so the action is offered
  // only where there is something to freeze.
  const canPublish =
    publishEnabled &&
    adopted &&
    (status.state === 'tracked-unpublished' || status.state === 'draft-in-progress');

  return (
    <div
      data-testid="resource-version-controls"
      className={`flex flex-wrap items-center gap-2 ${className}`}
    >
      {!actionOnly && (
        <VersionStatusChip
          status={status}
          onOpenHistory={adopted ? () => setHistoryOpen(true) : undefined}
        />
      )}

      {canPublish && (
        <button
          type="button"
          onClick={() => setPublishOpen(true)}
          data-testid="resource-publish-button"
          className="inline-flex items-center rounded-md border border-transparent bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
        >
          Publish
        </button>
      )}

      {adopted && entityId && (
        <>
          <VersionHistoryPanel
            open={historyOpen}
            onClose={() => setHistoryOpen(false)}
            entityType={entityType}
            entityId={entityId}
            resourceName={resourceName}
          />
          <PublishDialog
            open={publishEnabled && publishOpen}
            onClose={() => setPublishOpen(false)}
            entityType={entityType}
            entityId={entityId}
            resourceName={resourceName}
          />
        </>
      )}
    </div>
  );
}

export default ResourceVersionControls;
